import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Shield, FileText, FolderOpen, MessageSquare, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { cn } from '../../lib/utils';

export const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ policies: 0, quotes: 0, documents: 0, requests: 0 });
  const [loading, setLoading] = useState(true);

  // Auditors don't have insured-user data — redirect straight to /superadmin
  // (which has both the ACSEL analytics widget and a "Ver auditoría completa"
  // shortcut). This avoids showing them empty stat cards.
  useEffect(() => {
    if (user?.role === 'AUDITOR') {
      navigate('/superadmin', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const { data } = await api.get('/api/v1/stats/dashboard');
        setStats({
          policies: data.policies || 0,
          quotes: data.quotes || 0,
          documents: data.documents || 0,
          requests: data.requests || 0,
        });
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const cards = [
    { title: 'Polizas Activas', value: stats.policies, icon: Shield, gradient: 'from-[#052f6b] to-[#279ddc]' },
    { title: 'Cotizaciones', value: stats.quotes, icon: FileText, gradient: 'from-amber-500 to-orange-400' },
    { title: 'Documentos', value: stats.documents, icon: FolderOpen, gradient: 'from-emerald-500 to-teal-400' },
    { title: 'Solicitudes', value: stats.requests, icon: MessageSquare, gradient: 'from-violet-500 to-purple-400' },
  ];

  const quickActions = [
    { title: 'Cotizar Seguro', desc: 'Obten tu cotizacion al instante', icon: TrendingUp, href: '/products', iconBg: 'bg-[#052f6b]' },
    { title: 'Subir Documentos', desc: 'Carga segura y privada', icon: FolderOpen, href: '/documents', iconBg: 'bg-emerald-500' },
    { title: 'Nueva Solicitud', desc: 'Seguimiento en tiempo real', icon: MessageSquare, href: '/requests', iconBg: 'bg-violet-500' },
  ];

  return (
    <div className="space-y-7 animate-fade-in" data-testid="dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-4xl font-bold gradient-text leading-tight">
          Hola, {user?.name?.split(' ')[0] || 'Usuario'}
        </h1>
        <p className="text-lg text-muted-foreground mt-1">
          Gestiona tus seguros de forma rapida y segura
        </p>
      </div>

      {/* Stats - gradient cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className={cn('rounded-2xl p-4 sm:p-5 bg-gradient-to-br text-white shadow-lg', card.gradient)}
              data-testid={`stat-card-${card.title.toLowerCase()}`}
            >
              <Icon className="w-7 h-7 opacity-80 mb-3" strokeWidth={1.8} />
              <p className="text-3xl sm:text-4xl font-bold leading-none mb-1">
                {loading ? <span className="inline-block h-9 w-10 bg-white/20 animate-pulse rounded" /> : card.value}
              </p>
              <p className="text-sm text-white/80 font-medium">{card.title}</p>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Acciones Rapidas</h2>
        <div className="space-y-3">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <div
                key={action.title}
                className="bg-white rounded-2xl border border-border p-5 flex items-center gap-4 cursor-pointer active:scale-[0.98] transition-transform shadow-sm hover:shadow-md"
                data-testid={`quick-action-${action.title.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => navigate(action.href)}
              >
                <div className={cn('w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 text-white', action.iconBg)}>
                  <Icon className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold leading-tight">{action.title}</p>
                  <p className="text-base text-muted-foreground">{action.desc}</p>
                </div>
                <ArrowRight className="w-6 h-6 text-muted-foreground shrink-0" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Profile Completeness */}
      {user && user.profile_completeness < 1.0 && (
        <div
          className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-5 flex items-center gap-4 cursor-pointer active:scale-[0.99] transition-transform"
          data-testid="profile-completeness-card"
          onClick={() => navigate('/profile')}
        >
          <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center shrink-0">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-lg font-bold text-amber-900">Completa tu Perfil</p>
            <p className="text-base text-amber-700">
              {Math.round(user.profile_completeness * 100)}% completado
            </p>
          </div>
          <ArrowRight className="w-6 h-6 text-amber-500 shrink-0" />
        </div>
      )}
    </div>
  );
};
